/**
 * Client-emittable events.
 *
 * This list is deliberately tiny. Everything the API can observe for itself —
 * auth, payments, investigations, callbacks, documents, reports — is emitted by
 * the backend from `src/modules/telemetry`, and the ingest endpoint rejects
 * those names outright so they can't be forged from the browser.
 *
 * What's left is only what the server genuinely cannot see: a single-page app
 * resolves its own routes (a navigation produces no HTTP request), and the
 * employer report PDF is generated entirely in the browser.
 *
 * The full tracking plan lives in the backend repo at
 * `src/modules/telemetry/telemetry.events.ts`. Names here must match the
 * `CLIENT_EVENTS` allowlist there.
 */
export const EVENTS = {
    /** Report PDF assembled and saved client-side; no API call to observe. */
    EMPLOYER_REPORT_DOWNLOADED: 'employer_report:downloaded',
    /** PDF assembly threw in the browser — the server never sees this failure. */
    EMPLOYER_REPORT_DOWNLOAD_FAILED: 'employer_report:download_failed',
    /** Plan details sheet opened on the public one-pager. */
    PLAN_DETAILS_OPENED: 'plan:details_opened',
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

// Page views are not listed: each route emits its own `<page>:page_viewed`
// name, built by `toPageViewEvent()` in routes.ts and matched by pattern on the
// backend.
